import { throttle } from 'lodash-es';

import {
  isElectionsPage,
  isFreeHtmlPage,
  isGalleryPage,
  isHomePage,
  isHoroscopePages,
  isLatestNewsPage,
  isSportDataPage,
  isSportDomain,
} from '../../utils/aem-utils';
import { getCoords, hideElement, unHideElement } from '../../utils/dom-utils';
import { isMobile } from '../../utils/device-utils';
import { VIDEO_PLAYER_POSITIONS, VIDEO_PLAYER_STATE, VIDEO_PLAYER_STATUS } from '../video-player/constants';
import { isLandscape } from '../../utils/mediaquery-utils';

import {
  $videoCarouselSticky,
  $videoCarouselStickyContainer,
  $videoCarouselStickyHeader,
  $videoElemenCarouseltStickyContainerSpace,
  ABSTRACT_CLASSNAME,
  ASPECT_RATIO_CLASS_NAME,
  CAROUSEL_HOOK,
  SCROLL_EVENT_THROTTLE_TIME,
  VIDEO_CAROUSEL_SET_VALUE,
} from './constants';
import { stickyCarouselState } from './state';

let isAdvPlaying = true;
export const hasScrolledOut = (scrollingElement) => window.scrollY > scrollingElement;

// Function that checks if the element can go sticky in terms of page height.
// If the page width is greater than the height of the container it is possible to put the element in sticky
export const shouldSticky = (containerElement) => {
  if (
    isSportDataPage() ||
    isHoroscopePages() ||
    isFreeHtmlPage() ||
    isLatestNewsPage() ||
    isElectionsPage() ||
    (isHomePage() && isSportDomain())
  ) {
    return true;
  }
  if (isGalleryPage()) {
    const scrollElement = getCoords(document.querySelector(ABSTRACT_CLASSNAME)).top;
    return hasScrolledOut(scrollElement);
  }

  const scrollElement = getCoords(containerElement).top;
  return hasScrolledOut(scrollElement);
};

export const shouldInitCarousel = (stickyCarouselActive, stickyCarouselMode) => {
  if (!stickyCarouselActive) {
    return false;
  }

  switch (stickyCarouselMode) {
    case VIDEO_CAROUSEL_SET_VALUE.ALL:
      return true;
    case VIDEO_CAROUSEL_SET_VALUE.DESKTOP && !isMobile():
      return true;
    case VIDEO_CAROUSEL_SET_VALUE.MOBILE && isMobile():
      return true;
    default:
      return false;
  }
};

/**
 * Set video position
 * @param {*} element sticky video element
 * @param {*} player player instance
 * @param {*} position position to set ('sticky' or 'default')
 */
export const setVideoCarouselPosition = (element, player, position) => {
  stickyCarouselState.setState('playertype', $videoCarouselSticky.querySelector(CAROUSEL_HOOK).dataset.playertype);
  switch (position) {
    case VIDEO_PLAYER_POSITIONS.DEFAULT:
      if (element.dataset.state === VIDEO_PLAYER_POSITIONS.STICKY) {
        element.dataset.state = VIDEO_PLAYER_POSITIONS.DEFAULT;
        player.resize();
        hideElement($videoCarouselStickyHeader);
        player.setContext('page');
      }
      break;
    case VIDEO_PLAYER_POSITIONS.STICKY:
      element.dataset.state = VIDEO_PLAYER_POSITIONS.STICKY;
      player.setContext(stickyCarouselState.getState('playertype'));
      break;
    default:
      console.warn('[ STICKY - CAROUSEL ] video is not sticky and it is not in default state.');
      break;
  }
  player.resize();
};

// Destruction of the StickyCarousel
export const destroyStickyCarousel = (observer, CarouselStickyContainer, CarouselSticky, videoPlayer) => {
  stickyCarouselState.setState('isClosed', true);
  observer.unobserve(CarouselStickyContainer);
  setVideoCarouselPosition(CarouselSticky, videoPlayer, VIDEO_PLAYER_POSITIONS.DEFAULT);
  $videoElemenCarouseltStickyContainerSpace.classList.remove(ASPECT_RATIO_CLASS_NAME);
  console.warn('[ STICKY - CAROUSEL ] Destroy the StickyCarousel, for an error or for clos the banner.');
};

// Landscape management, if the element goes into landscape the sticky is not delivered
export const settingLandscape = (videoPlayer, observerCarouselSticky) => {
  if (isMobile()) {
    let currentViewportWidth = window.innerWidth;
    let currentViewportHeight = window.innerHeight;
    if (isLandscape()) {
      destroyStickyCarousel(observerCarouselSticky, $videoCarouselStickyContainer, $videoCarouselSticky, videoPlayer);
    }
    window.addEventListener(
      'resize',
      throttle(() => {
        if (window.innerWidth !== currentViewportWidth || window.innerHeight !== currentViewportHeight) {
          if (isLandscape()) {
            destroyStickyCarousel(
              observerCarouselSticky,
              $videoCarouselStickyContainer,
              $videoCarouselSticky,
              videoPlayer
            );
          }
          currentViewportWidth = window.innerWidth;
          currentViewportHeight = window.innerHeight;
        }
      }, SCROLL_EVENT_THROTTLE_TIME)
    );
  }
};

export const isStickyCarouselActive = () => {
  return $videoCarouselSticky?.dataset.state === VIDEO_PLAYER_POSITIONS.STICKY;
};

export const toggleStickyHeaderX = (videoPlayer) => {
  videoPlayer.on(VIDEO_PLAYER_STATUS.PLAY, () => {
    if (isStickyCarouselActive()) {
      unHideElement($videoCarouselStickyHeader);
    }
    isAdvPlaying = false;
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.AD_ERROR, () => {
    if (isStickyCarouselActive()) {
      unHideElement($videoCarouselStickyHeader);
    }
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.PAUSE, () => {
    hideElement($videoCarouselStickyHeader);
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.AD_LOADED, () => {
    isAdvPlaying = true;
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.AD_PLAY, () => {
    hideElement($videoCarouselStickyHeader);
    isAdvPlaying = true;
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.AD_PAUSE, () => {
    hideElement($videoCarouselStickyHeader);
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.AD_COMPLETE, () => {
    isAdvPlaying = false;
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.BEFORE_PLAY, () => {
    hideElement($videoCarouselStickyHeader);
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.READY, () => {
    hideElement($videoCarouselStickyHeader);
  });

  videoPlayer.on(VIDEO_PLAYER_STATUS.RESIZE, () => {
    if (isStickyCarouselActive() && !isAdvPlaying) {
      unHideElement($videoCarouselStickyHeader);
    } else {
      hideElement($videoCarouselStickyHeader);
    }
  });
};
